<script setup lang="ts">
    /**
     * What the user wrote while the agent answers (spec
     * resumable-chat-streams, F2), sent together when the answer ends. At the
     * bottom of the conversation, above the input and outside the scrolling
     * list: the answer in flight holds its place there, anchored under the
     * question, and a message added after it would sit below that hold, out
     * of view. Each one goes back to the input to be changed, or leaves the
     * queue.
     */
    defineProps<{
        messages: string[]
    }>()

    const emit = defineEmits<{
        edit: [index: number]
        remove: [index: number]
    }>()
</script>

<template>
    <ol class="pk-chatbot-queued" :aria-label="$t('label.queuedMessages')">
        <li
            v-for="(text, index) in messages"
            :key="`${index}:${text}`"
            class="pk-chatbot-queued__item">
            <span class="pk-chatbot-queued__label">
                {{ $t('label.queued') }}
            </span>
            <div class="pk-chatbot-queued__bubble">
                <p class="pk-chatbot-queued__text">{{ text }}</p>
                <VvButtonGroup class="pk-chatbot-queued__actions">
                    <VvButton
                        icon="ri:edit-line"
                        modifiers="action-quiet"
                        :title="$t('action.edit')"
                        :aria-label="$t('action.edit')"
                        class="w-24 h-24"
                        @click="emit('edit', index)" />
                    <VvButton
                        icon="ri:close-line"
                        modifiers="action-quiet"
                        :title="$t('action.removeFromQueue')"
                        :aria-label="$t('action.removeFromQueue')"
                        class="w-24 h-24"
                        @click="emit('remove', index)" />
                </VvButtonGroup>
            </div>
        </li>
    </ol>
</template>

<style lang="scss">
    .pk-chatbot-queued {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: var(--spacing-8);
        width: 100%;
        max-width: 768px;
        margin: 0 auto;
        padding: 0 var(--spacing-md) var(--spacing-sm);
        list-style: none;

        &__item {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: var(--spacing-4);
            max-width: 85%;
        }

        &__label {
            font-size: var(--text-12);
            color: var(--color-word-3);
        }

        // A user bubble not sent yet: the shape of the user's own, drawn in
        // outline and quieter, so it never reads as part of the conversation
        &__bubble {
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-8);
            padding: var(--spacing-8) var(--spacing-8) var(--spacing-8)
                var(--spacing-12);
            border: 1px dashed var(--color-surface-4);
            border-radius: var(--rounded-xl) var(--rounded-xl) 0
                var(--rounded-xl);
            background-color: var(--color-surface);
            color: var(--color-word-2);
        }

        &__text {
            flex: 1;
            min-width: 0;
            margin: 0;
            font-size: var(--text-14);
            line-height: var(--leading-normal);
            white-space: pre-wrap;
            overflow-wrap: anywhere;
        }

        &__actions {
            flex-shrink: 0;
        }
    }
</style>
